import React, { useState, useEffect, useCallback } from 'react';
import { 
  ArrowRight, Sparkles, Check, X, Mic, BarChart3, Calendar, Zap, 
  Shield, Globe, Lock, Eye, Send, ChevronDown, ChevronUp, Menu, Settings2
} from 'lucide-react';
import PrismMonolith from './PrismMonolith';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const LandingPage = ({ onGetStarted }) => {
  const [openFaq, setOpenFaq] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { openPreferences } = useCookieConsent();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleFaq = (idx) => setOpenFaq(openFaq === idx ? null : idx);

  const navLinks = [
    { href: '#how-it-works', label: 'How it works' },
    { href: '#features', label: 'Features' },
    { href: '#pricing', label: 'Pricing' },
  ];

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      {/* Morphing Floating Navbar */}
      <div
        className="sticky top-4 z-50 flex justify-center px-4"
        style={{ pointerEvents: 'none' }}
      >
        <nav
          className="pointer-events-auto"
          style={{
            maxWidth: isScrolled ? '375px' : '1400px',
            width: '100%',
            background: isScrolled
              ? 'rgba(250, 250, 249, 0.4)'
              : 'rgba(250, 250, 249, 0.88)',
            backdropFilter: isScrolled
              ? 'blur(20px) saturate(180%)'
              : 'blur(12px)',
            WebkitBackdropFilter: isScrolled
              ? 'blur(20px) saturate(180%)'
              : 'blur(12px)',
            border: isScrolled
              ? '1px solid rgba(28, 25, 23, 0.12)'
              : '1px solid rgba(28, 25, 23, 0.06)',
            borderRadius: isScrolled ? '60px' : '20px',
            padding: isScrolled ? '6px 16px' : '14px 24px',
            boxShadow: isScrolled
              ? '0 8px 32px rgba(28, 25, 23, 0.12), 0 2px 8px rgba(28, 25, 23, 0.06)'
              : '0 4px 20px rgba(28, 25, 23, 0.04)',
            transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          data-testid="morphing-navbar"
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <img
                src="/stilos-logo.png"
                alt="Stilos"
                className="rounded-lg object-cover"
                style={{
                  width: isScrolled ? '28px' : '36px',
                  height: isScrolled ? '28px' : '36px',
                  transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
              <span
                className="font-display font-semibold tracking-tight"
                style={{
                  fontSize: isScrolled ? '13px' : '16px',
                  transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Stilos
              </span>
            </div>

            {/* Desktop Nav Links - hidden when scrolled */}
            <div
              className="hidden md:flex items-center gap-7"
              style={{
                opacity: isScrolled ? 0 : 1,
                pointerEvents: isScrolled ? 'none' : 'auto',
                transition: 'opacity 300ms ease',
                position: isScrolled ? 'absolute' : 'relative',
                overflow: 'hidden',
                width: isScrolled ? 0 : 'auto',
              }}
            >
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors font-medium whitespace-nowrap"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Right side: burger + CTA */}
            <div className="flex items-center gap-2">
              {/* Burger menu - visible on mobile always, desktop when scrolled */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`flex items-center justify-center rounded-full hover:bg-[rgba(28,25,23,0.05)] transition-colors ${
                  !isScrolled ? 'md:hidden' : ''
                }`}
                style={{
                  width: isScrolled ? '32px' : '36px',
                  height: isScrolled ? '32px' : '36px',
                  transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                data-testid="navbar-menu-btn"
              >
                {mobileMenuOpen ? (
                  <X style={{ width: isScrolled ? '16px' : '18px', height: isScrolled ? '16px' : '18px', transition: 'all 500ms ease' }} />
                ) : (
                  <Menu style={{ width: isScrolled ? '16px' : '18px', height: isScrolled ? '16px' : '18px', transition: 'all 500ms ease' }} />
                )}
              </button>

              {/* CTA Button */}
              <button
                onClick={onGetStarted}
                className="font-medium text-white rounded-full whitespace-nowrap"
                style={{
                  background: 'var(--color-coral)',
                  padding: isScrolled ? '5px 14px' : '10px 20px',
                  fontSize: isScrolled ? '11px' : '14px',
                  transition: 'all 500ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={(e) => e.target.style.background = '#d4654e'}
                onMouseLeave={(e) => e.target.style.background = 'var(--color-coral)'}
                data-testid="nav-get-started-btn"
              >
                {isScrolled ? 'Login' : 'Start free'}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile / Scrolled Dropdown Menu */}
        {mobileMenuOpen && (
          <div
            className="pointer-events-auto absolute mt-2"
            style={{
              top: isScrolled ? '52px' : '68px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: isScrolled ? '375px' : 'calc(100% - 2rem)',
              maxWidth: '1400px',
              background: 'rgba(250, 250, 249, 0.5)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(28, 25, 23, 0.1)',
              borderRadius: '16px',
              padding: '12px 20px',
              boxShadow: '0 8px 32px rgba(28, 25, 23, 0.1)',
              animation: 'fadeSlideDown 200ms ease-out',
            }}
            data-testid="mobile-nav-menu"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2.5 text-sm font-medium text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-2 mt-1 border-t border-[rgba(28,25,23,0.06)]">
              <button
                onClick={() => { setMobileMenuOpen(false); onGetStarted(); }}
                className="w-full text-sm font-medium py-2.5 text-[var(--color-coral)] hover:text-[var(--color-charcoal)] transition-colors text-left"
              >
                Sign in
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--color-surface)] text-xs font-medium text-[var(--color-muted)] mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[var(--color-coral)]" />
            Voice-first AI content — now in open beta
          </div>
          <h1 className="headline-hero mb-6">
            Your <span className="accent">voice.</span><br />
            AI-ready.
          </h1>
          <p className="text-base text-[var(--color-muted)] max-w-2xl mx-auto mb-3 leading-relaxed">
            58% of Google searches now end without a single click. AI answers the question before your link ever appears. Your competitors are being cited as trusted sources.
          </p>
          <p className="text-base font-medium text-[var(--color-charcoal)] mb-10">
            You're invisible. <strong>But not for long.</strong>
          </p>
          
          {/* AI Input Preview */}
          <div className="max-w-xl mx-auto mb-4">
            <div className="ai-input-container">
              <div className="ai-input-icon">
                <Sparkles className="w-4 h-4" />
              </div>
              <input type="text" placeholder="Record a 2-min voice note → get a full AI-optimized article..." readOnly className="cursor-pointer" onClick={onGetStarted} data-testid="hero-chat-input" />
              <button className="btn btn-primary py-2 px-4" onClick={onGetStarted} data-testid="hero-chat-submit">
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[var(--color-muted)] mt-2">
              Voice-first content creation with built-in AEO & GEO optimization
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-4">
            <button onClick={onGetStarted} className="btn btn-primary px-6" data-testid="hero-get-started-btn">
              Start building your pillar — free
            </button>
            <a href="#how-it-works" className="btn btn-secondary px-6">
              See Stilos in action <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <p className="text-xs text-[var(--color-muted)]">
            No credit card required &middot; 3 free articles &middot; Cancel anytime
          </p>
        </div>
      </section>

      {/* ============================================
          STATS BAR
          ============================================ */}
      <section className="py-14 border-y border-[var(--color-border)]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-16 text-center">
            <div>
              <div className="font-display text-4xl font-light tracking-tight text-[var(--color-charcoal)]">58%</div>
              <div className="text-xs text-[var(--color-muted)] mt-1">of Google searches end<br />with zero clicks</div>
            </div>
            <div>
              <div className="font-display text-4xl font-light tracking-tight text-[var(--color-coral)]">40%</div>
              <div className="text-xs text-[var(--color-muted)] mt-1">of Google results now show<br />AI Overview answers</div>
            </div>
            <div>
              <div className="font-display text-4xl font-light tracking-tight text-[var(--color-charcoal)]">2min</div>
              <div className="text-xs text-[var(--color-muted)] mt-1">Voice note to full<br />AI-optimized article</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          THE SHIFT — Problem Section with Google Mockup
          ============================================ */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">THE PROBLEM</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">The shift already happened</h2>
            <p className="text-[var(--color-muted)] max-w-2xl mx-auto text-sm leading-relaxed">
              Google's results page now shows 4 ads, then an AI Overview, then <em>maybe</em> your link. 
              ChatGPT, Perplexity, Gemini, and Bing Copilot are answering your customers' questions right now — and citing your competitors as the trusted source.
            </p>
          </div>
          
          {/* Google Search Mockup */}
          <div className="max-w-md mx-auto mb-14">
            <div className="browser-mockup">
              <div className="browser-mockup-bar">
                <div className="browser-dot red"></div>
                <div className="browser-dot yellow"></div>
                <div className="browser-dot green"></div>
                <div className="browser-url">google.com/search?q=best+content+strategy</div>
              </div>
              <div className="p-5 space-y-2.5">
                <div className="text-[10px] font-semibold text-[var(--color-muted)] uppercase tracking-wider mb-3">Google Search Results — 2025</div>
                {['Ad', 'Ad', 'Ad', 'Ad'].map((_, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50/60 text-xs text-amber-700 font-medium">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 font-bold">Ad</span>
                    <span className="text-[var(--color-muted)]">Sponsored result #{i+1}</span>
                  </div>
                ))}
                <div className="px-3 py-2.5 rounded-lg bg-blue-50/60 text-xs">
                  <div className="font-semibold text-blue-700 mb-1">AI Overview</div>
                  <div className="text-[10px] text-blue-600/70">Sources: <span className="underline">your competitor</span>, <span className="underline">your competitor</span>...</div>
                </div>
                <div className="px-3 py-2 rounded-lg bg-[var(--color-surface)] text-xs text-[var(--color-muted)] flex items-center gap-2">
                  <span>&#8593;</span> Your link <span className="text-[10px] italic">(if they even scroll this far)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PERSONAS — Built for builders like you
          ============================================ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">WHO IT'S FOR</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Built for builders like you</h2>
            <p className="text-[var(--color-muted)] max-w-2xl mx-auto text-sm leading-relaxed">
              Whether you're a solo founder, a lean marketing team, or an agency scaling client work — Stilos was designed for the way you actually work.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-5 mb-8">
            {[
              {
                eyebrow: 'Overwhelmed Owner',
                title: 'The SMB Founder',
                desc: 'You know content drives growth — but between product, sales, and operations, who has 6 hours to write a blog post?',
                solution: 'Record a 2-minute voice note on your commute → get a full, AI-optimized article published to your blog before lunch. Zero writing required.'
              },
              {
                eyebrow: 'Growth Marketer',
                title: 'The Content Strategist',
                desc: 'Your board wants 10x content output. Your budget says otherwise. You need to scale without scaling headcount.',
                solution: 'Full content workflow from ideation to multi-platform publishing for less than one freelance article per month. AEO/GEO baked in.'
              },
              {
                eyebrow: 'Agency Lead',
                title: 'The Client Manager',
                desc: "Your clients need results yesterday. You need efficiency across 10+ accounts. Brand voice consistency isn't optional.",
                solution: 'Manage multiple brand voice profiles, generate client content at scale, and publish directly across platforms.'
              }
            ].map((persona, idx) => (
              <div key={idx} className="card p-7">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: '#f5f5f4' }}>
                  <span className="text-base font-bold" style={{ color: '#e7735c' }}>{idx + 1}</span>
                </div>
                <p className="font-label text-[var(--color-muted)] mb-1">{persona.eyebrow}</p>
                <h3 className="font-display text-base font-medium mb-3">{persona.title}</h3>
                <p className="text-xs text-[var(--color-muted)] mb-4 leading-relaxed">{persona.desc}</p>
                <div className="p-3 rounded-xl bg-[var(--color-surface)]">
                  <p className="text-xs font-semibold text-[var(--color-coral)] mb-1">With Stilos:</p>
                  <p className="text-xs text-[var(--color-charcoal)] leading-relaxed">{persona.solution}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Anti-persona */}
          <div className="card p-5 max-w-2xl mx-auto text-center" style={{boxShadow: 'inset 0 0 0 1px var(--color-border)'}}>
            <p className="text-xs text-[var(--color-muted)] leading-relaxed">
              <strong className="text-[var(--color-charcoal)]">Who Stilos is NOT for:</strong> Enterprise teams with 50-person content departments, brands that don't care about authentic voice, or anyone looking for mass-produced AI slop. We built this for ambitious businesses that punch above their weight with <em>quality</em> content.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          OLD vs NEW — Comparison
          ============================================ */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">OLD VS NEW</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">The rules changed. Your strategy must too.</h2>
            <p className="text-[var(--color-muted)] text-sm">The old playbook is broken. Here's how the winners are playing now.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card p-8">
              <h3 className="font-display text-lg mb-5 text-[var(--color-muted)]">Old World — Traditional SEO</h3>
              <ul className="space-y-3 text-sm text-[var(--color-charcoal)]">
                {[
                  'Write keyword-stuffed content → hope for page 1',
                  "SEO = gaming Google's algorithm",
                  'Goal: Rank on page 1 for keywords',
                  'Hire freelancers, wait weeks for content',
                  'Measure: organic clicks (declining 15% YoY)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[var(--color-border)] mt-0.5 flex-shrink-0">&#8594;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-8" style={{boxShadow: 'inset 0 0 0 2px var(--color-coral)'}}>
              <h3 className="font-display text-lg mb-5 text-[var(--color-coral)]">New World — AEO/GEO with Stilos</h3>
              <ul className="space-y-3 text-sm text-[var(--color-charcoal)]">
                {[
                  'Create authoritative content → AI cites you as trusted source',
                  'AEO/GEO = be the answer AI delivers',
                  'Goal: AI Overview citation + featured source',
                  'Speak your expertise, publish in minutes',
                  'Measure: AI citations + trust authority (growing 50%+ YoY)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[var(--color-coral)] mt-0.5 flex-shrink-0">&#8594;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          HOW IT WORKS — 3 Steps
          ============================================ */}
      <section id="how-it-works" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">HOW IT WORKS</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">From voice note to published pillar in 3 steps</h2>
            <p className="text-[var(--color-muted)] text-sm">No writing skills needed. No SEO expertise required. Just your voice and your expertise.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-8 left-[calc(16.67%+20px)] right-[calc(16.67%+20px)] h-[1px] bg-[var(--color-border)]" />
            
            {[
              { num: 1, icon: Mic, title: 'Speak Your Expertise', desc: "Record a 2-minute voice note about your topic. Talk like you'd explain it to a client. That's all Stilos needs." },
              { num: 2, icon: Sparkles, title: 'AI Transforms & Optimizes', desc: 'Stilos structures your ideas into a full article, adds AEO/GEO optimization and an SEO score — preserving your authentic voice.' },
              { num: 3, icon: Send, title: 'Publish Everywhere', desc: 'One click publishes to WordPress, Medium, LinkedIn, and more. Schedule it, forget it, and watch your AI citations grow.' },
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="text-center relative">
                  <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center text-sm font-bold text-white bg-[var(--color-charcoal)] mb-5 relative z-10">
                    {step.num}
                  </div>
                  <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-4 bg-[var(--color-surface)]">
                    <Icon className="w-5 h-5 text-[var(--color-charcoal)]" />
                  </div>
                  <h3 className="font-display text-sm font-medium mb-2">{step.title}</h3>
                  <p className="text-xs text-[var(--color-muted)] leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <button onClick={onGetStarted} className="btn btn-primary px-6">
              Try it free — record your first voice note
            </button>
          </div>
        </div>
      </section>

      {/* ============================================
          FEATURES — Build pillars, not posts
          ============================================ */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">FEATURES</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Build pillars, not posts</h2>
            <p className="text-[var(--color-muted)] max-w-2xl mx-auto text-sm leading-relaxed">
              Every article you create with Stilos becomes a pillar — content that AI trusts, humans read, and your business stands on.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                icon: Mic, title: 'Voice-First Creation', badge: 'Key Differentiator',
                desc: "Speak your ideas for 2 minutes and get a complete, publication-ready article. No writing block, no blank screens. Your authentic expertise, transcribed, structured, and amplified by AI."
              },
              {
                icon: BarChart3, title: 'AEO & GEO Optimization', badge: 'Key Differentiator',
                desc: "Answer Engine Optimization and Generative Engine Optimization built into every article. Stilos structures your content to be the source AI models cite — with FAQ formatting, entity structuring, and answer-engine optimization."
              },
              {
                icon: Send, title: 'Multi-Platform Publishing', badge: 'Key Differentiator',
                desc: "Publish directly to WordPress from one dashboard. Schedule posts, manage your content calendar, and maintain consistent presence. Medium, LinkedIn and more coming soon."
              },
              {
                icon: Eye, title: 'Real-Time SEO Analysis',
                desc: "Instant SEO scoring with actionable recommendations as you create. Keyword density, readability, meta optimization — all in real-time."
              },
              {
                icon: Sparkles, title: 'AI Article Generation',
                desc: "From a single title to a full, SEO-optimized article in seconds. Choose your tone, set your brand voice, define your target audience — and let Stilos generate content that sounds like you."
              },
              {
                icon: Globe, title: 'Brand Voice Profiles', badge: 'Key Differentiator',
                desc: "Save your brand's tone, style, industry and audience. Every article generated matches your unique voice. Use the brand report to align your entire marketing strategy."
              },
              {
                icon: Calendar, title: 'Content Calendar',
                desc: "Plan, schedule, and track your content pipeline. Visual calendar view with drag-and-drop scheduling across all your platforms."
              },
              {
                icon: Zap, title: 'PWA — Create Anywhere', badge: 'Key Differentiator',
                desc: "Progressive Web App works beautifully on any device — phone, tablet, desktop. Record voice notes on your morning walk, edit on your tablet, publish from your desk."
              },
            ].map((feature, idx) => (
              <div key={idx} className="card card-hover p-7">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--color-surface)]">
                    <feature.icon className="w-5 h-5 text-[var(--color-coral)]" />
                  </div>
                  {feature.badge && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(232,115,92,0.08)] text-[var(--color-coral)] font-semibold">
                      {feature.badge}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-base font-medium mb-2">{feature.title}</h3>
                <p className="text-xs text-[var(--color-muted)] leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          COMPETITOR COMPARISON TABLE
          ============================================ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">COMPARISON</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Why Stilos wins the comparison</h2>
            <p className="text-[var(--color-muted)] text-sm">Other tools do one thing well. Stilos gives you the <em>entire workflow</em> — voice to publish.</p>
          </div>
          
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--color-surface)]">
                    <th className="text-left p-4 font-medium text-[var(--color-muted)] text-xs">Feature</th>
                    <th className="p-4 font-medium text-xs text-[var(--color-coral)]">Stilos</th>
                    <th className="p-4 font-medium text-xs text-[var(--color-muted)]">Jasper AI</th>
                    <th className="p-4 font-medium text-xs text-[var(--color-muted)]">Rank.ai</th>
                    <th className="p-4 font-medium text-xs text-[var(--color-muted)]">Audiense</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { f: 'Voice-First Creation', s: true, j: false, r: false, a: false },
                    { f: 'AEO / GEO Optimization', s: true, j: false, r: 'partial', a: false },
                    { f: 'Multi-Platform Publish', s: true, j: false, r: false, a: false },
                    { f: 'AI Article Generation', s: true, j: true, r: true, a: false },
                    { f: 'SEO Analysis & Scoring', s: true, j: 'partial', r: true, a: false },
                    { f: 'Brand Voice Profiles', s: true, j: true, r: false, a: false },
                    { f: 'Brand Reporting', s: true, j: false, r: false, a: false },
                    { f: 'Content Calendar', s: true, j: false, r: false, a: false },
                    { f: 'PWA (Mobile-Ready)', s: true, j: false, r: false, a: false },
                    { f: 'Full Workflow (Idea→Publish)', s: true, j: false, r: false, a: false },
                    { f: 'Starting Price', s: '€29/mo', j: '$49/mo', r: '$99/mo', a: '$696/mo' },
                  ].map((row, idx) => (
                    <tr key={idx} className="border-t border-[var(--color-border)]">
                      <td className="p-4 text-xs font-medium">{row.f}</td>
                      <td className="p-4 text-center">{renderCompareCell(row.s, true)}</td>
                      <td className="p-4 text-center">{renderCompareCell(row.j)}</td>
                      <td className="p-4 text-center">{renderCompareCell(row.r)}</td>
                      <td className="p-4 text-center">{renderCompareCell(row.a)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-[var(--color-muted)] p-4 border-t border-[var(--color-border)]">
              Comparison based on publicly available information as of 2025. Features may vary by plan.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          TRUST & SECURITY
          ============================================ */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">TRUST & SECURITY</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Your data. Your voice. Your business.</h2>
            <p className="text-[var(--color-muted)] max-w-xl mx-auto text-sm">
              We amplify your expertise — we never replace it. Your content is your property.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Lock, title: 'Privacy First', desc: 'Your content stays yours. GDPR-compliant, encrypted in transit and at rest. Never shared with third parties.' },
              { icon: Sparkles, title: 'AI Assists, Never Replaces', desc: 'Your voice is the foundation. Stilos amplifies it with structure and optimization — the ideas and authority are always yours.' },
              { icon: BarChart3, title: 'AI-Ready From Day One', desc: 'Every piece of content is automatically optimized for AI citation with structured formatting and answer-engine optimization.' },
              { icon: Globe, title: 'Global-Ready', desc: 'English-first with EU and emerging market expansion planned. Your content strategy works wherever your customers are.' },
              { icon: Shield, title: 'No Model Training', desc: 'Your content is never used to train AI models. What you create belongs to you — full intellectual property rights, always.' },
              { icon: Zap, title: '99.9% Uptime', desc: 'Built on reliable cloud infrastructure with real-time monitoring. Your content workflow never stops.' },
            ].map((item, idx) => (
              <div key={idx} className="card p-7 text-center">
                <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
                  <item.icon className="w-5 h-5 text-[var(--color-coral)]" />
                </div>
                <h4 className="font-display font-medium text-sm mb-1.5">{item.title}</h4>
                <p className="text-xs text-[var(--color-muted)] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          MANIFESTO / BLOCKQUOTE
          ============================================ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="card p-10" style={{background: 'var(--color-charcoal)', color: 'white', boxShadow: 'none'}}>
            <div className="text-3xl text-[var(--color-coral)] mb-4 font-serif">"</div>
            <h2 className="text-xl md:text-2xl font-serif mb-6 text-white leading-snug">
              Nobody owns "AI search optimization for small business" yet.
            </h2>
            <p className="text-sm text-stone-400 leading-relaxed mb-4">
              Stilos sits in a blue ocean: the intersection of <strong className="text-white">affordable voice-first content creation</strong> and <strong className="text-white">AEO/GEO optimization</strong> specifically for SMBs and lean teams. The big players (Jasper, Surfer, SEMrush) focus on enterprise. The cheap tools produce generic AI slop.
            </p>
            <p className="text-sm text-stone-400 leading-relaxed mb-6">
              This is the window. The businesses that build their content pillars <em>now</em> — while AI search is still forming its source graph — will be the ones AI cites for years. Early authority compounds. <strong className="text-white">The question isn't whether to start. It's whether you can afford to wait.</strong>
            </p>
            <div className="flex flex-wrap gap-2">
              {['Voice-First', 'AEO/GEO', 'Full Workflow', 'SMB Pricing', 'PWA', 'Multi-Platform'].map((tag, idx) => (
                <span key={idx} className="text-[10px] px-2.5 py-1 rounded-full font-medium" style={{background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)'}}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PRICING
          ============================================ */}
      <section id="pricing" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">PRICING</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Simple, transparent pricing</h2>
            <p className="text-[var(--color-muted)] text-sm">Start free. Upgrade when you're ready. No hidden fees.</p>
            <p className="text-xs text-[var(--color-muted)] mt-1">All plans include AEO/GEO optimization and real-time SEO scoring.</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-5">
            {[
              {
                name: 'Free', sub: 'Perfect for testing the waters', price: '0',
                features: [
                  { f: '3 articles (lifetime)', on: true }, { f: '10 credits', on: true },
                  { f: 'Basic SEO analysis', on: true }, { f: 'AEO/GEO scoring', on: true },
                  { f: '1 platform connection', on: true }, { f: 'Community support', on: true },
                  { f: 'Voice-to-Article AI', on: false }, { f: 'Brand voice profiles', on: false },
                  { f: 'Brand reporting', on: false },
                ],
                cta: 'Start free'
              },
              {
                name: 'Starter', sub: 'For the overwhelmed owner', price: '29',
                features: [
                  { f: '10 articles/month', on: true }, { f: '100 credits', on: true },
                  { f: 'AI article generation', on: true }, { f: 'AEO/GEO scoring', on: true },
                  { f: '3 platform connections', on: true }, { f: 'Content calendar', on: true },
                  { f: 'Email support', on: true }, { f: 'Voice-to-Article AI', on: false },
                  { f: 'Brand voice profiles', on: false }, { f: 'Brand reporting', on: false },
                ],
                cta: 'Start 14-day trial'
              },
              {
                name: 'Professional', sub: 'For growth marketers scaling content', price: '79',
                featured: true,
                features: [
                  { f: '50 articles/month', on: true }, { f: '500 credits', on: true },
                  { f: 'Voice-to-Article AI', on: true }, { f: 'Full AEO/GEO optimization', on: true },
                  { f: 'All platform integrations', on: true }, { f: 'Brand voice profiles (3)', on: true },
                  { f: 'Brand reporting', on: true }, { f: 'Content calendar + scheduling', on: true },
                  { f: 'Advanced analytics', on: true }, { f: 'Priority support', on: true },
                ],
                cta: 'Go Pro — 14-day trial'
              },
              {
                name: 'Enterprise', sub: 'For agencies managing multiple clients', price: '199',
                features: [
                  { f: '200 articles/month', on: true }, { f: '2,000 credits', on: true },
                  { f: 'Everything in Professional', on: true }, { f: 'Custom AI workflows', on: true },
                  { f: 'Unlimited brand voice profiles', on: true }, { f: 'White-label options', on: true },
                  { f: 'Team collaboration', on: true }, { f: 'Dedicated account manager', on: true },
                  { f: 'SLA guarantee', on: true },
                ],
                cta: 'Talk to sales'
              },
            ].map((tier, idx) => (
              <div key={idx} className={`pricing-card ${tier.featured ? 'featured' : ''}`} data-testid={`pricing-${tier.name.toLowerCase()}`}>
                {tier.featured && <div className="font-label text-[var(--color-coral)] mb-3">MOST POPULAR</div>}
                <div className="pricing-tier">{tier.name}</div>
                <p className="text-[10px] text-[var(--color-muted)] mb-3">{tier.sub}</p>
                <div className="pricing-price">&#8364;{tier.price}<span>/mo</span></div>
                <div className="pricing-features">
                  {tier.features.map((feature, fidx) => (
                    <div key={fidx} className={`pricing-feature ${!feature.on ? 'opacity-40' : ''}`}>
                      {feature.on ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      <span>{feature.f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={onGetStarted} className={`btn w-full ${tier.featured ? 'btn-accent' : 'btn-secondary'}`}>
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>
          
          <p className="text-center text-xs text-[var(--color-muted)] mt-8">
            <strong>Credits never expire.</strong> Top up anytime: 100 credits &#8364;9 &middot; 500 credits &#8364;39 &middot; 1,000 credits &#8364;69
          </p>
          <p className="text-center text-xs text-[var(--color-muted)] mt-2">
            Annual billing available — save 20% on any paid plan
          </p>
        </div>
      </section>

      {/* ============================================
          ROADMAP
          ============================================ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="font-label text-[var(--color-coral)] mb-3 inline-block">ROADMAP</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">Where we're going</h2>
            <p className="text-[var(--color-muted)] text-sm">Stilos is built for the long game. Here's the roadmap we're betting on.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                phase: 'Phase 1 — Launch', title: 'Prove the Model',
                items: [
                  { name: 'Open Beta Launch', status: 'live' },
                  { name: 'WordPress Publishing', status: 'live' },
                  { name: 'AEO/GEO Optimization', status: 'live' },
                  { name: 'Brand Voice Profiles', status: 'live' },
                  { name: 'Brand Reporting', status: 'live' },
                ],
                focus: 'English-speaking markets, early adopters, content creator community'
              },
              {
                phase: 'Phase 2 — Growth', title: 'Scale the Engine',
                items: [
                  { name: 'Medium/LinkedIn Publishing', status: 'q2' },
                  { name: 'SEO Content Clusters', status: 'q2' },
                  { name: 'EU Market Expansion', status: 'q3' },
                  { name: 'Agency Partnerships', status: 'q4' },
                ],
                focus: 'Content marketing flywheel, community building, retention'
              },
              {
                phase: 'Phase 3 — Dominate', title: 'Own the Category',
                items: [
                  { name: 'Emerging Markets', status: '2026' },
                  { name: 'Advanced Integrations', status: '2026' },
                  { name: 'Team Collaboration', status: '2026' },
                  { name: 'Revenue Target: €80K MRR', status: '2026' },
                ],
                focus: 'Category leadership, brand authority, "AI content for SMBs = Stilos"'
              },
            ].map((phase, idx) => (
              <div key={idx} className="card p-7">
                <p className="font-label text-[var(--color-muted)] mb-1">{phase.phase}</p>
                <h3 className="font-display text-base font-medium mb-5">{phase.title}</h3>
                <div className="space-y-2.5 mb-5">
                  {phase.items.map((item, iidx) => (
                    <div key={iidx} className="flex items-center justify-between">
                      <span className="text-xs">{item.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                        item.status === 'live' 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-[var(--color-surface)] text-[var(--color-muted)]'
                      }`}>
                        {item.status === 'live' ? 'Live' : item.status.toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-[var(--color-muted)] italic">Focus: {phase.focus}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          FINAL CTA — Animated Prism Card
          ============================================ */}
      <section className="py-24 px-6" data-testid="cta-prism-section">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden relative" style={{ height: '600px', background: '#0A0A0A' }}>
            {/* Layer 1: WebGL Monolith Prism */}
            <PrismMonolith className="absolute inset-0" />

            {/* Layer 2: Content Overlay */}
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-6 max-w-3xl leading-tight">
                From invisible to indispensable
              </h2>
              <p className="text-white/60 mb-3 max-w-2xl mx-auto text-base leading-relaxed">
                AI search engines are building their source graph <em className="text-white/80">right now</em>. The businesses that create authoritative content today will be the ones AI cites for years to come.
              </p>
              <p className="text-white/60 mb-10 max-w-2xl mx-auto text-base leading-relaxed">
                Early authority compounds. Every day you wait, your competitors are recording voice notes, publishing AI-optimized articles, and claiming the citations that could be yours.
              </p>

              {/* StarBorder CTA Button */}
              <button
                onClick={onGetStarted}
                className="star-border-container"
                data-testid="cta-get-started-btn"
              >
                <div className="star-border-gradient star-border-top" />
                <div className="star-border-gradient star-border-bottom" />
                <span className="star-border-inner">
                  Start your first pillar — free <ArrowRight className="w-4 h-4" />
                </span>
              </button>

              <p className="text-xs text-white/40 mt-6">
                No credit card required &middot; 3 free articles &middot; Your voice, AI-ready in 2 minutes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          FOOTER
          ============================================ */}
      <footer className="py-16 px-6 border-t border-[var(--color-border)]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-5 gap-10 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <img src="/stilos-logo.png" alt="Stilos" className="w-7 h-7 rounded-lg object-cover" />
                <span className="font-display font-semibold text-sm">Stilos</span>
              </div>
              <p className="text-xs text-[var(--color-muted)] leading-relaxed mb-2">AI-powered content creation for small businesses.</p>
              <p className="text-[10px] text-[var(--color-muted)]">Part of Themelio</p>
            </div>
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Product</h4>
              <div className="space-y-2.5">
                <a href="/features" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Features</a>
                <a href="/pricing" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Pricing</a>
                <a href="/how-it-works" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">How it Works</a>
                <a href="/roadmap" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Roadmap</a>
                <a href="/system-status" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">System Status</a>
              </div>
            </div>
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Company</h4>
              <div className="space-y-2.5">
                <a href="/about" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">About</a>
                <a href="/contact" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Contact</a>
                <a href="/impressum" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Impressum</a>
              </div>
            </div>
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Resources</h4>
              <div className="space-y-2.5">
                <a href="/knowledge-base" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Knowledge Base</a>
                <a href="/appsumo" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">AppSumo Deal</a>
              </div>
            </div>
            <div>
              <h4 className="font-display text-xs font-semibold mb-4 text-[var(--color-charcoal)]">Legal</h4>
              <div className="space-y-2.5">
                <a href="/privacy" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Privacy Policy</a>
                <a href="/terms" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Terms of Service</a>
                <a href="/cookies" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Cookie Policy</a>
                <a href="/dpa" className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors">Data Processing</a>
                <button onClick={openPreferences} className="block text-xs text-[var(--color-muted)] hover:text-[var(--color-charcoal)] transition-colors text-left" data-testid="footer-cookie-settings-btn">
                  Cookie Settings
                </button>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-[var(--color-border)] flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-[var(--color-muted)]">&copy; 2026 Stilos by Themelio. All rights reserved.</p>
            <p className="text-[10px] text-[var(--color-muted)]">Built in the EU. GDPR compliant.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* Helper: render comparison table cell */
const renderCompareCell = (val, isStilos = false) => {
  if (typeof val === 'string') {
    return <span className={`text-xs font-medium ${isStilos ? 'text-[var(--color-coral)]' : ''}`}>{val}</span>;
  }
  if (val === true) {
    return <Check className={`w-4 h-4 mx-auto ${isStilos ? 'text-[var(--color-coral)]' : 'text-green-600'}`} />;
  }
  if (val === 'partial') {
    return <span className="text-xs text-amber-600 font-medium">Partial</span>;
  }
  return <X className="w-4 h-4 mx-auto text-[var(--color-border)]" />;
};

export default LandingPage;
